import React, { useEffect, useState, useRef } from "react";
import { Html5Qrcode } from "html5-qrcode";
import link from "../Images/link.svg";
import qeIcon from "../Images/qr.svg";
import LanguageSelector from '../Components/LanguageSelector'
import newLogoIcon from '../Images/newLogo.svg'
import { useTranslation } from "react-i18next";


const ScanQr = () => {
    const [scanning, setScanning] = useState(false);
    const { t, i18n } = useTranslation();
    const scannerRef = useRef(null);
    const readerRef = useRef(null);
    const isInitializingRef = useRef(false);

    // Cleanup function to stop scanner and clear state
    const cleanupScanner = async (resetInitFlag = true) => {
        console.log("🧹 [QR Scanner] Starting cleanup...", {
            hasScanner: !!scannerRef.current,
            resetInitFlag,
            isInitializing: isInitializingRef.current
        });
        
        if (scannerRef.current) {
            try {
                console.log("🧹 [QR Scanner] Stopping existing scanner...");
                await scannerRef.current.stop();
                console.log("✅ [QR Scanner] Scanner stopped successfully");
            } catch (err) {
                console.error("❌ [QR Scanner] Error stopping scanner:", err);
            }
            scannerRef.current = null;
        }
        // Clear any leftover content in the reader div
        const element = document.getElementById("reader");
        if (element) {
            console.log("🧹 [QR Scanner] Clearing reader element innerHTML");
            element.innerHTML = "";
        } else {
            console.warn("⚠️ [QR Scanner] Reader element not found during cleanup");
        }
        if (resetInitFlag) {
            isInitializingRef.current = false;
            console.log("🧹 [QR Scanner] Reset initialization flag");
        }
        console.log("✅ [QR Scanner] Cleanup completed");
    };

    // Cleanup on component mount (when navigating back)
    useEffect(() => {
        console.log("🔄 [QR Scanner] Component mounted, resetting state...");
        
        // Check what's in storage BEFORE clearing
        const beforeInitialMandatoryParams = localStorage.getItem("initialMandatoryParams");
        const beforeInitialUrlSearch = localStorage.getItem("initialUrlSearch");
        const beforeQrScanNavLocal = localStorage.getItem("qrScanNavigation");
        const beforeQrScanNavSession = sessionStorage.getItem("qrScanNavigation");
        
        console.log("📦 [QR Scanner] BEFORE CLEARING - Storage contents:");
        console.log("  - initialMandatoryParams:", beforeInitialMandatoryParams);
        console.log("  - initialUrlSearch:", beforeInitialUrlSearch);
        console.log("  - qrScanNavigation (localStorage):", beforeQrScanNavLocal);
        console.log("  - qrScanNavigation (sessionStorage):", beforeQrScanNavSession);
        
        // Clear ALL URL-related storage items when on scan page
        // This ensures that when a new QR code is scanned, it's treated as a fresh initial load
        localStorage.removeItem("initialMandatoryParams");
        localStorage.removeItem("initialUrlSearch");
        localStorage.removeItem("qrScanNavigation");
        sessionStorage.removeItem("qrScanNavigation");
        
        // Verify removal
        const afterInitialMandatoryParams = localStorage.getItem("initialMandatoryParams");
        const afterInitialUrlSearch = localStorage.getItem("initialUrlSearch");
        const afterQrScanNavLocal = localStorage.getItem("qrScanNavigation");
        const afterQrScanNavSession = sessionStorage.getItem("qrScanNavigation");
        
        console.log("🧹 [QR Scanner] AFTER CLEARING - Storage contents:");
        console.log("  - initialMandatoryParams:", afterInitialMandatoryParams, afterInitialMandatoryParams ? "❌ STILL EXISTS!" : "✅ REMOVED");
        console.log("  - initialUrlSearch:", afterInitialUrlSearch, afterInitialUrlSearch ? "❌ STILL EXISTS!" : "✅ REMOVED");
        console.log("  - qrScanNavigation (localStorage):", afterQrScanNavLocal, afterQrScanNavLocal ? "❌ STILL EXISTS!" : "✅ REMOVED");
        console.log("  - qrScanNavigation (sessionStorage):", afterQrScanNavSession, afterQrScanNavSession ? "❌ STILL EXISTS!" : "✅ REMOVED");
        
        // Reset scanning state and cleanup any leftover scanner instances
        setScanning(false);
        cleanupScanner();

        // Cleanup on unmount
        return () => {
            console.log("🧹 [QR Scanner] Component unmounting, cleaning up...");
            cleanupScanner();
        };
    }, []);

    const requestCameraPermission = async () => {
        console.log("📷 [QR Scanner] Requesting camera permission...");
        try {
            // First, cleanup any existing scanner
            await cleanupScanner();
            
            if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
                console.error("❌ [QR Scanner] Browser doesn't support camera API");
                alert("⚠️ المتصفح لا يدعم الكاميرا. الرجاء فتح الصفحة في Chrome أو Safari.");
                return;
            }

            console.log("📷 [QR Scanner] Requesting getUserMedia...");
            await navigator.mediaDevices.getUserMedia({ video: true });
            console.log("✅ [QR Scanner] Camera permission granted");

            // Small delay to ensure cleanup is complete
            setTimeout(() => {
                console.log("📷 [QR Scanner] Setting scanning to true");
                setScanning(true);
            }, 100);
        } catch (err) {
            console.error("❌ [QR Scanner] Camera permission error:", {
                name: err.name,
                message: err.message,
                stack: err.stack,
                error: err
            });
            alert("⚠️ الرجاء السماح للتطبيق باستخدام الكاميرا لمسح رمز الـ QR.");
        }
    };

    useEffect(() => {
        console.log("🔄 [QR Scanner] useEffect triggered", {
            scanning,
            isInitializing: isInitializingRef.current
        });

        if (scanning && !isInitializingRef.current) {
            console.log("🚀 [QR Scanner] Starting scanner initialization...");
            
            // Wait for the DOM element to be fully rendered
            const initScanner = async () => {
                console.log("🔍 [QR Scanner] initScanner called");
                
                // Prevent multiple simultaneous initializations
                if (isInitializingRef.current) {
                    console.warn("⚠️ [QR Scanner] Already initializing, skipping...");
                    return;
                }

                // Check if element exists and is in the DOM
                const element = document.getElementById("reader");
                if (!element) {
                    console.warn("⚠️ [QR Scanner] Reader element not found, retrying in 100ms...");
                    // If element doesn't exist yet, wait a bit more
                    setTimeout(initScanner, 100);
                    return;
                }

                console.log("✅ [QR Scanner] Reader element found:", element);

                // Mark as initializing
                isInitializingRef.current = true;
                console.log("🔒 [QR Scanner] Set initialization flag to true");

                // Clean up any existing scanner instance and clear the element
                // Don't reset init flag during cleanup since we're about to initialize
                console.log("🧹 [QR Scanner] Cleaning up before initialization...");
                await cleanupScanner(false);
                
                // Small delay to ensure cleanup is complete before initializing new scanner
                console.log("⏳ [QR Scanner] Waiting 150ms after cleanup...");
                await new Promise(resolve => setTimeout(resolve, 150));

                // Clear the element's innerHTML to remove any leftover content
                element.innerHTML = "";
                console.log("🧹 [QR Scanner] Cleared element innerHTML");

                try {
                    console.log("🔧 [QR Scanner] Creating new Html5Qrcode instance...");
                    const qrCodeScanner = new Html5Qrcode("reader");
                    scannerRef.current = qrCodeScanner;
                    isInitializingRef.current = false;
                    console.log("✅ [QR Scanner] Html5Qrcode instance created");

                    console.log("🎬 [QR Scanner] Starting scanner...");
                    qrCodeScanner
                        .start(
                            { facingMode: "environment" },
                            { fps: 10, qrbox: { width: 250, height: 250 } },
                            (decodedText) => {
                                console.log("✅ [QR Scanner] QR Code decoded successfully:", decodedText);
                                try {
                                    const url = new URL(decodedText);
                                    console.log("✅ [QR Scanner] Valid URL detected, navigating to:", url.href);
                                    
                                    // Set flag to indicate this navigation came from a QR scan
                                    // This allows LandingPage to distinguish between scan-based and manual URL changes
                                    // Set it in both sessionStorage and localStorage for reliability
                                    sessionStorage.setItem("qrScanNavigation", "true");
                                    localStorage.setItem("qrScanNavigation", "true");
                                    console.log("✅ [QR Scanner] Set qrScanNavigation flag in sessionStorage and localStorage");
                                    console.log("✅ [QR Scanner] Flag value:", sessionStorage.getItem("qrScanNavigation"));
                                    
                                    if (scannerRef.current) {
                                        scannerRef.current.stop().catch(() => { });
                                        scannerRef.current = null;
                                    }
                                    isInitializingRef.current = false;
                                    
                                    // Small delay to ensure flag is set before navigation
                                    setTimeout(() => {
                                        window.location.href = url.href;
                                    }, 50);
                                } catch (urlError) {
                                    console.error("❌ [QR Scanner] Invalid URL format:", {
                                        decodedText,
                                        error: urlError
                                    });
                                    alert(`الكود غير صالح: ${decodedText}`);
                                    if (scannerRef.current) {
                                        scannerRef.current.stop().catch(() => { });
                                        scannerRef.current = null;
                                    }
                                    isInitializingRef.current = false;
                                    setScanning(false);
                                }
                            },
                            (errorMessage) => {
                                // Log scanning errors (these are normal during scanning)
                                console.log("📡 [QR Scanner] Scanning error (normal):", errorMessage);
                            }
                        )
                        .then(() => {
                            console.log("✅ [QR Scanner] Scanner started successfully!");
                        })
                        .catch((err) => {
                            console.error("❌ [QR Scanner] Scanner start failed:", {
                                name: err.name,
                                message: err.message,
                                stack: err.stack,
                                error: err,
                                fullError: JSON.stringify(err, Object.getOwnPropertyNames(err))
                            });
                            
                            if (err.name === "NotReadableError") {
                                console.error("❌ [QR Scanner] Camera not readable - may be in use by another app");
                                alert(
                                    "⚠️ لم يمكن تشغيل الكاميرا. تأكد من إغلاق أي تطبيق آخر يستخدم الكاميرا (مثل واتساب أو فيسبوك)."
                                );
                            } else if (err.name === "NotAllowedError") {
                                console.error("❌ [QR Scanner] Camera permission denied");
                                alert("❌ تم رفض إذن الكاميرا.");
                            } else {
                                console.error("❌ [QR Scanner] Unknown camera error");
                                alert("⚠️ حدث خطأ أثناء تشغيل الكاميرا.");
                            }
                            if (scannerRef.current) {
                                scannerRef.current.stop().catch(() => { });
                                scannerRef.current = null;
                            }
                            isInitializingRef.current = false;
                            setScanning(false);
                        });
                } catch (err) {
                    console.error("❌ [QR Scanner] Failed to initialize scanner:", {
                        name: err.name,
                        message: err.message,
                        stack: err.stack,
                        error: err,
                        fullError: JSON.stringify(err, Object.getOwnPropertyNames(err))
                    });
                    isInitializingRef.current = false;
                    setScanning(false);
                }
            };

            // Use requestAnimationFrame to ensure DOM is ready, then small delay for safety
            console.log("⏳ [QR Scanner] Scheduling initScanner with requestAnimationFrame...");
            requestAnimationFrame(() => {
                console.log("⏳ [QR Scanner] requestAnimationFrame callback, scheduling setTimeout...");
                setTimeout(() => {
                    console.log("⏳ [QR Scanner] setTimeout callback, calling initScanner...");
                    initScanner();
                }, 100);
            });
        } else {
            console.log("⏸️ [QR Scanner] Not initializing:", {
                scanning,
                isInitializing: isInitializingRef.current
            });
        }

        return () => {
            // Cleanup on unmount or when scanning changes
            console.log("🧹 [QR Scanner] useEffect cleanup triggered");
            cleanupScanner();
        };
    }, [scanning]);

    return (
        <div dir={i18n.language == "ar" || i18n.language == "ur" ? 'rtl' : 'ltr'} className="h-screen w-screen flex-col gap-4 flex justify-center items-center">
            <div className="px-3 flex items-start w-full " >
                <LanguageSelector />
            </div>
            <img src={newLogoIcon} className="w-56 sm:mt-0 mt-[30%]" />
            <img src={link} alt="link icon" />
            <div className="text-center text-stone-700 text-base font-medium font-cairo leading-normal">
                {t('LinkIsWrong')}
            </div>

            <button
                onClick={requestCameraPermission}
                className="flex rounded-2xl px-5 py-3 bg-[#E57200] flex-row gap-2 items-center"
            >
                <img src={qeIcon} alt="qr icon" />
                <div className="text-white text-base font-medium font-cairo leading-normal">
                    {t('scanTheQr')}
                </div>
            </button>

            {scanning && (
                <div
                    id="reader"
                    ref={readerRef}
                    className="mt-4 w-[300px] h-[300px] border-2 border-stone-300 rounded-lg"
                ></div>
            )}
        </div>
    );
};

export default ScanQr;